import type { HsVector3View } from '@sibe/hyperscene';

/** A measurement point in world coordinates. */
export type HsMeasurementPoint = readonly [number, number, number];

/** What a measurement computes from its picked points; `point` reads the coordinates of one point. */
export type HsMeasurementMode = 'distance' | 'angle' | 'polyline' | 'area' | 'point';
/**
 * Display unit of linear and area values. Values are kept in world units, which are meters: the API delivers BIM
 * (IFC, RVM) and point clouds in meters, and photogrammetry in the units its import matrix produces.
 */
export type HsMeasurementUnit = 'm' | 'cm' | 'mm';

export function hsMeasureDistance(a: HsVector3View, b: HsVector3View): number {
  const dx: number = a.x - b.x;
  const dy: number = a.y - b.y;
  const dz: number = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Angle at `vertex` between the rays to `a` and `c`, in degrees; 0 when a ray has no length. */
export function hsMeasureAngleDegrees(a: HsVector3View, vertex: HsVector3View, c: HsVector3View): number {
  const ax: number = a.x - vertex.x;
  const ay: number = a.y - vertex.y;
  const az: number = a.z - vertex.z;
  const bx: number = c.x - vertex.x;
  const by: number = c.y - vertex.y;
  const bz: number = c.z - vertex.z;
  const lengthA: number = Math.sqrt(ax * ax + ay * ay + az * az);
  const lengthB: number = Math.sqrt(bx * bx + by * by + bz * bz);
  if (lengthA < 0.000001 || lengthB < 0.000001) {
    return 0;
  }
  const cosine: number = Math.max(-1, Math.min(1, (ax * bx + ay * by + az * bz) / (lengthA * lengthB)));
  return (Math.acos(cosine) * 180) / Math.PI;
}

export function hsMeasurePolylineLength(points: readonly HsVector3View[]): number {
  let length: number = 0;
  for (let index: number = 1; index < points.length; index++) {
    length += hsMeasureDistance(points[index - 1], points[index]);
  }
  return length;
}

/** Area of a planar polygon in 3D (Newell's method); 0 for fewer than three points. */
export function hsMeasurePolygonArea(points: readonly HsVector3View[]): number {
  if (points.length < 3) {
    return 0;
  }
  let sumX: number = 0;
  let sumY: number = 0;
  let sumZ: number = 0;
  for (let index: number = 0; index < points.length; index++) {
    const current: HsVector3View = points[index];
    const next: HsVector3View = points[(index + 1) % points.length];
    sumX += (current.y - next.y) * (current.z + next.z);
    sumY += (current.z - next.z) * (current.x + next.x);
    sumZ += (current.x - next.x) * (current.y + next.y);
  }
  return 0.5 * Math.sqrt(sumX * sumX + sumY * sumY + sumZ * sumZ);
}

/** Points a mode needs before it completes on its own; 0 means the user finishes it. */
export function hsMeasurementTargetPoints(mode: HsMeasurementMode): number {
  return mode === 'point' ? 1 : mode === 'distance' ? 2 : mode === 'angle' ? 3 : 0;
}

/** How far apart two points are along the world axes, across the ground (`horizontal`) and up (`vertical`). */
export interface HsMeasurementDeltas {
  dx: number;
  dy: number;
  dz: number;
  horizontal: number;
  vertical: number;
}

export function hsMeasureDeltas(a: HsVector3View, b: HsVector3View): HsMeasurementDeltas {
  const dx: number = b.x - a.x;
  const dy: number = b.y - a.y;
  const dz: number = b.z - a.z;
  return { dx, dy, dz, horizontal: Math.hypot(dx, dz), vertical: Math.abs(dy) };
}

/**
 * The factor that turns world units of a model into real meters, from a length measured in the model and its known
 * real length; 1 for lengths that cannot calibrate (zero or not finite).
 */
export function hsCalibrationScale(measured: number, real: number): number {
  return Number.isFinite(measured) && Number.isFinite(real) && measured > 0 && real > 0 ? real / measured : 1;
}

/** The mode's value for the points, or null while there are too few of them. */
export function hsMeasure(mode: HsMeasurementMode, points: readonly HsVector3View[]): number | null {
  if (mode === 'point') {
    // The elevation; the coordinates themselves are the point.
    return points.length >= 1 ? points[0].y : null;
  }
  if (mode === 'distance') {
    return points.length >= 2 ? hsMeasureDistance(points[0], points[1]) : null;
  }
  if (mode === 'angle') {
    return points.length >= 3 ? hsMeasureAngleDegrees(points[0], points[1], points[2]) : null;
  }
  if (mode === 'polyline') {
    return points.length >= 2 ? hsMeasurePolylineLength(points) : null;
  }
  return points.length >= 3 ? hsMeasurePolygonArea(points) : null;
}

export function hsConvertLinearMeasurement(valueInMeters: number, unit: HsMeasurementUnit): number {
  return unit === 'cm' ? valueInMeters * 100 : unit === 'mm' ? valueInMeters * 1000 : valueInMeters;
}

export function hsConvertAreaMeasurement(valueInSquareMeters: number, unit: HsMeasurementUnit): number {
  return unit === 'cm'
    ? valueInSquareMeters * 10000
    : unit === 'mm'
    ? valueInSquareMeters * 1000000
    : valueInSquareMeters;
}

/** Decimals that keep a millimeter resolution in each unit. */
const UNIT_DECIMALS: Readonly<Record<HsMeasurementUnit, number>> = { m: 3, cm: 1, mm: 0 };

/** A measured value with its unit: `12.345 m`, `1.5 cm²`, `90.00°`. */
export function hsFormatMeasurement(mode: HsMeasurementMode, value: number, unit: HsMeasurementUnit): string {
  if (mode === 'angle') {
    return `${value.toFixed(2)}°`;
  }
  if (mode === 'point') {
    return `elevation ${hsConvertLinearMeasurement(value, unit).toFixed(UNIT_DECIMALS[unit])} ${unit}`;
  }
  if (mode === 'area') {
    return `${hsConvertAreaMeasurement(value, unit).toFixed(UNIT_DECIMALS[unit])} ${unit}²`;
  }
  return `${hsConvertLinearMeasurement(value, unit).toFixed(UNIT_DECIMALS[unit])} ${unit}`;
}

/** The mode's name for labels: `Distance`, `Angle`, `Polyline`, `Area`. */
export function hsMeasurementModeLabel(mode: HsMeasurementMode): string {
  return mode === 'distance'
    ? 'Distance'
    : mode === 'angle'
    ? 'Angle'
    : mode === 'polyline'
    ? 'Polyline'
    : mode === 'point'
    ? 'Point'
    : 'Area';
}
